import React, { useState, useCallback } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { marpolSelectors } from "../../../../redux/app/marpol";
import { IStore } from "../../../../redux/store";
import { TEMFBackendRecord, EEMFRecordCode } from "redux/app/emf/@types";
import moment from "moment";
import DataTable from "./components/DataTable";
import { Tabs, Tab } from "@blueprintjs/core";

type OwnProps = {
	disabled: boolean;
};
type EMFDocsProps = OwnProps;

function EMFDocs(props: EMFDocsProps & PropsFromRedux) {
	const { disabled, marpolDocs } = props;
	const [selectedTabId, setSelectedTabId] = useState(EEMFRecordCode.PACKAGING);

	let docs = marpolDocs.filter(
		(record: TEMFBackendRecord) => record.recordCode === selectedTabId
	);

	const handleExportCSV = () => {
		if (!docs.length) return;

		const columns = generateColumnsForEmRecords(
			selectedTabId as EEMFRecordCode,
			docs
		);

		// Prepare CSV content
		const headers = columns.map((col) => col.name).join(",");
		const rows = docs.map((row: Record<string, any>, index) => {
			return columns
				.map((col) => {
					const value = col.getValue
						? col.getValue(col.key, row[col.key], index)
						: row[col.key] || "";
					// Escape quotes and wrap in quotes if contains comma
					const formatted = String(value).replace(/"/g, '""');
					return formatted.includes(",") ? `"${formatted}"` : formatted;
				})
				.join(",");
		});

		const csvContent = [headers, ...rows].join("\n");

		// Create and trigger download
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`emf_records_${selectedTabId}_${
				new Date().toISOString().split("T")[0]
			}.csv`
		);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	function generateColumnsForEmRecords(
		recordCode: EEMFRecordCode,
		data: TEMFBackendRecord[]
	): Array<{
		key: string;
		name: string;
		width?: number;
		getValue?: (key: string, value: any, index: number) => any;
	}> {
		// Helper function to create a column definition
		const createColumn = (key: string, name: string, width: number = 100) => ({
			key,
			name,
			width,
		});

		// Base columns that appear in all record types
		const baseColumns = [
			{
				key: "sno",
				name: "S.No",
				width: 70,
				getValue: (key: any, value: any, index: number) => index + 1,
			},
		];

		switch (recordCode) {
			case EEMFRecordCode.CLEANING_CHEMICALS:
				return [
					...baseColumns,
					{
						key: "reportingDate",
						name: "Reporting Month (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					{
						key: "selectedDate",
						name: "Operation Date (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					createColumn("selectedTime", "Operation Time (UTC)", 150),
					createColumn("section", "Section", 120),
					createColumn("category", "Category", 120),
					createColumn("substance_property", "Substance Property", 150),
					createColumn("substance", "Substance", 150),
					createColumn("quantity", "Quantity", 100),
					createColumn("units", "Units", 80),
					createColumn("event", "Actions/Event", 120),
					createColumn("remarks", "Remarks", 150),
				];
			case EEMFRecordCode.PACKAGING:
				return [
					...baseColumns,
					{
						key: "reportingDate",
						name: "Reporting Month (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					{
						key: "selectedDate",
						name: "Operation Date (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					createColumn("selectedTime", "Operation Time (UTC)", 150),
					createColumn("portName", "Port Name", 120),
					createColumn("supplierName", "Supplier Name", 120),
					{
						key: "receivedDate",
						name: "Received Date (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					createColumn("packagingStorage", "Packaging", 120),
					createColumn(
						"materialRetainedOnboard",
						"Material Retained onboard(M³)",
						180
					),
					{
						key: "biodegradable",
						name: "Biodegradable",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return value ? "Yes" : "No";
						},
					},
					createColumn("remarks", "Remarks", 150),
				];
			case EEMFRecordCode.REFRIGERANTS:
				return [
					...baseColumns,
					{
						key: "reportingDate",
						name: "Reporting Month (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					{
						key: "selectedDate",
						name: "Operation Date (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					createColumn("selectedTime", "Operation Time (UTC)", 150),
					createColumn("refrigerantType", "Refrigerant Type", 150),
					createColumn("refrigerantOtherType", "Other Refrigerant", 150),
					createColumn("purchased", "Purchased(Kgs)", 120),
					createColumn("consumed", "Consumed(Kgs)", 120),
					createColumn("leaks", "Leaks due to Breakdowns(Kgs)", 180),
					createColumn("remarks", "Remarks", 150),
				];
			case EEMFRecordCode.VOC_NMVOC:
				return [
					...baseColumns,
					{
						key: "reportingDate",
						name: "Reporting Month (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					{
						key: "selectedDate",
						name: "Operation Date (UTC)",
						width: 70,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					createColumn("selectedTime", "Operation Time (UTC)", 150),
					createColumn("emissionType", "VOC/NMVOC", 120),
					{
						key: "releaseStartDate",
						name: "Start Date of Release (UTC)",
						width: 180,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					createColumn("releaseStartTime", "Start Time of Release (UTC)", 180),
					{
						key: "releaseEndDate",
						name: "End Date of Release (UTC)",
						width: 180,
						getValue: (key: any, value: any, index: number) => {
							return moment(value).format("DD-MM-YYYY");
						},
					},
					createColumn("releaseEndTime", "End Time of Release (UTC)", 180),
					createColumn("releaseDuration", "Duration of Release (Mins)", 150),
					createColumn(
						"tankPressureAtStartOfRelease",
						"Tank Pressure at Start of Release(mmWG)",
						220
					),
					createColumn(
						"tankPressureAtEndOfRelease",
						"Tank Pressure at End of Release(mmWG)",
						220
					),
					createColumn(
						"diameterOfReleasePipelineOpening",
						"Diameter of Release pipeline opening(m)",
						220
					),
					createColumn(
						"lengthOfReleasePipelineFromTank",
						"Length of Release Pipeline from Tank(m)",
						220
					),
					createColumn("vocNmvoc", "VOC/NMVOC %", 120),
					createColumn("remarks", "Remarks", 150),
				];
		}
	}

	const columns = generateColumnsForEmRecords(
		selectedTabId as EEMFRecordCode,
		marpolDocs || []
	);

	const handleTabChange = (newTabId: EEMFRecordCode) => {
		setSelectedTabId(newTabId);
	};

	const DocsPanel = useCallback(
		() => (
			<DataTable columns={columns} data={docs || []} className="shadow-sm" />
		),
		[columns, docs]
	);

	return (
		<div className="w-full p-0">
			{/*  EMF Body */}
			<div className="w-full bg-white rounded-lg shadow-sm">
				<div className="flex flex-col flex-1 max-w-full max-h-full mx-auto">
					{/* Table Header with Export button */}
					<div className="w-full flex items-center justify-between  border-b border-gray-200">
						<div className="text-base font-medium text-gray-700">
							Environmental Management Records
						</div>
						<div className="flex items-end gap-4">
							<button
								onClick={handleExportCSV}
								className="px-4 py-2 mb-1.5 text-sm font-medium text-white bg-blue-600  hover:bg-blue-700 focus:outline-none  disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={!docs.length}
								title={!docs.length ? "No records to export" : "Export to CSV"}
							>
								Export CSV
							</button>
						</div>
					</div>
					{/* Tabs - Table Container */}
					<div className="w-full overflow-hidden">
						<Tabs
							id="TabsExample"
							onChange={handleTabChange}
							selectedTabId={selectedTabId}
							large={false}
							renderActiveTabPanelOnly={true}
						>
							<Tab
								id={EEMFRecordCode.PACKAGING}
								title="Packaging"
								panel={<DocsPanel />}
							/>
							<Tab
								id={EEMFRecordCode.REFRIGERANTS}
								title="Refrigerants"
								panel={<DocsPanel />}
							/>
							<Tab
								id={EEMFRecordCode.CLEANING_CHEMICALS}
								title="Cleaning Chemicals"
								panel={<DocsPanel />}
							/>
							<Tab
								id={EEMFRecordCode.VOC_NMVOC}
								title="VOC/NMVOC"
								panel={<DocsPanel />}
							/>
						</Tabs>
					</div>
					{/* Table Footer with Record Count */}
					<div className="w-full p-4 border-t border-gray-200 bg-gray-50">
						<span className="text-sm text-gray-600">
							Showing {docs.length} record{docs.length !== 1 ? "s" : ""}
						</span>
					</div>
				</div>
			</div>
			{/* Footer */}
			<div className="px-3 py-5 mt-6 text-right">
				<span className="ml-auto text-sm font-medium text-gray-600">
					Powered by - <strong className="font-bold">Volteo Maritime</strong>
				</span>
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore) {
	const marpolStore = marpolSelectors._getStore(store);

	return {
		marpolDocs: marpolSelectors.getDocs(marpolStore) as TEMFBackendRecord[],
	};
}
function mapDispatchToProps(dispatch: Dispatch) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(EMFDocs);
